import { FC, ReactNode, useEffect, useState } from 'react';
import { captureException } from '@sentry/react';
import { Button, Col, Modal, Row, Switch, Typography } from 'antd';
import { TbAuth2Fa, TbCircle, TbLockCog, TbMinusVertical } from 'react-icons/tb';

import { authorizedAxiosInstance } from '@/clients';
import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { useEntitlements } from '@/hooks/useEntitlements';
import { PlanTag } from '@/shared';
import { toast } from '@/shared/components/toasts/PlatformToast';
import { useUserStore } from '@/stores';

import EnableTFA from './views/01-EnableTFA';
import DisableTFA from './views/02-DisableTFA';

// interface TFAProps {}

const TFA: FC = (): JSX.Element => {
    const [open, setOpen] = useState(false);

    const { selectedUser: user, session } = useUserStore();
    const { token } = useAntDesignTheme();
    const { hasFeature } = useEntitlements();
    const canUseTFA = hasFeature('tfa');

    const [enabled, setEnabled] = useState(user?.otpEnabled === true);
    const [verified, setVerified] = useState(user?.otpEnabled === true && session?.tfaVerified === true);
    const [saving, setSaving] = useState(false);

    const [qrCode, setQrCode] = useState<string>('');

    const enableTFA = async () => {
        if (enabled || saving || !canUseTFA) {
            return;
        }

        try {
            setSaving(true);

            const res = await authorizedAxiosInstance.post<{ qrCodeDataUrl?: string }>('/api/tfa/enable');
            const qrCodeDataUrl = res?.data?.qrCodeDataUrl;

            if (!qrCodeDataUrl) {
                throw new Error('Missing TFA setup QR code.');
            }

            setQrCode(qrCodeDataUrl);
            setOpen(true);
        } catch (error) {
            captureException(error);
            toast('Failed to enable TFA');
        } finally {
            setSaving(false);
        }
    };

    useEffect(() => {
        const nextEnabled = user?.otpEnabled === true;

        setEnabled(nextEnabled);
        setVerified(nextEnabled && session?.tfaVerified === true);

        if (nextEnabled) {
            setQrCode('');
        }
    }, [session?.tfaVerified, user?.otpEnabled]);

    const modalViewMap = {
        enableTFA: (
            <EnableTFA
                open={open}
                setOpen={setOpen}
                setEnabled={setEnabled}
                qrCode={qrCode}
                setQrCode={setQrCode}
                setVerified={setVerified}
            />
        ),
        disableTFA: <DisableTFA setEnabled={setEnabled} setOpen={setOpen} setVerified={setVerified} />,
    } as Record<string, ReactNode>;

    return (
        <>
            <Row align='top'>
                <Col flex='auto'>
                    <Typography.Title id='settings-account-security-two-factor' level={4}>
                        <TbAuth2Fa style={{ marginRight: '0.5rem', verticalAlign: '-0.1rem' }} />
                        Two-Factor Authentication
                        <PlanTag plan='plus' style={{ marginLeft: '0.5rem', verticalAlign: '0.125rem' }} />
                    </Typography.Title>
                    <Typography.Paragraph>
                        <span style={{ color: token.colorTextTertiary }}>
                            Enable two-factor authentication for increased account security.
                        </span>
                    </Typography.Paragraph>
                </Col>
                <Col flex='none'>
                    <Switch
                        disabled={enabled || saving || !canUseTFA}
                        checked={enabled}
                        loading={saving}
                        style={{ verticalAlign: '-0.75rem' }}
                        checkedChildren={<TbMinusVertical style={{ verticalAlign: '-0.1rem' }} />}
                        unCheckedChildren={<TbCircle style={{ verticalAlign: '-0.1rem' }} />}
                        onClick={enableTFA}
                    />
                </Col>
            </Row>
            {enabled && (
                <Button
                    block
                    style={{ marginTop: '0.5rem', marginBottom: '1.5rem' }}
                    type='dashed'
                    onClick={() => setOpen(true)}
                >
                    <TbLockCog size='1rem' style={{ verticalAlign: '-0.175rem', marginRight: '0.25rem' }} />
                    Manage Two-Factor Authentication
                </Button>
            )}
            <Modal
                mask={false}
                open={open}
                title='Manage Two-Factor Authentication'
                footer={null}
                width='32rem'
                onCancel={() => setOpen(false)}
            >
                {modalViewMap[verified && qrCode === '' ? 'disableTFA' : 'enableTFA']}
            </Modal>
        </>
    );
};

export default TFA;
